import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Globe, EnvelopeSimple, Key, ArrowLeft } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("email"); // email | code | done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState("");

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setInfo("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setInfo("If that email exists, a reset code has been sent. Check your inbox.");
      setStage("code");
    } catch (err) {
      setError(err.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { email, otp, new_password: newPw });
      setStage("done");
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-12" data-testid="forgot-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-emerald-700 mb-6" data-testid="forgot-back-link">
          <ArrowLeft size={14} weight="bold" /> Back to sign in
        </Link>
        <div className="flex items-center justify-center gap-2 mb-8">
          <BrandLogo size={40} iconSize={22}
            markClassName="bg-emerald-700"
            nameClassName="font-heading text-xl font-semibold text-stone-900" />
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm">
          {stage === "email" && (
            <>
              <h1 className="font-heading text-xl sm:text-2xl font-semibold text-stone-900 mb-1">Reset your password</h1>
              <p className="font-body text-sm text-stone-500 mb-5">We&apos;ll email you a one-time code valid for 15 minutes.</p>
              <form onSubmit={requestCode} className="space-y-4">
                <div>
                  <label className="text-xs text-stone-500 font-body block mb-1.5">Your email</label>
                  <div className="relative">
                    <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 text-sm font-body focus:outline-none focus:border-emerald-500"
                      data-testid="forgot-email-input" />
                  </div>
                </div>
                {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="forgot-error">{error}</p>}
                <button type="submit" disabled={loading}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  data-testid="forgot-request-btn">
                  {loading ? "Sending..." : "Send code"}
                </button>
              </form>
            </>
          )}

          {stage === "code" && (
            <>
              <h1 className="font-heading text-xl sm:text-2xl font-semibold text-stone-900 mb-1">Enter your code</h1>
              <p className="font-body text-sm text-stone-500 mb-5">{info}</p>
              <form onSubmit={submitReset} className="space-y-4">
                <div>
                  <label className="text-xs text-stone-500 font-body block mb-1.5">6-digit code</label>
                  <div className="relative">
                    <Key size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <input type="text" required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000" inputMode="numeric" autoComplete="one-time-code"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 text-sm font-mono tracking-widest focus:outline-none focus:border-emerald-500"
                      data-testid="forgot-otp-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-stone-500 font-body block mb-1.5">New password</label>
                  <input type="password" required minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-body focus:outline-none focus:border-emerald-500"
                    data-testid="forgot-newpw-input" />
                </div>
                {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="forgot-error">{error}</p>}
                <button type="submit" disabled={loading}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5 rounded-xl disabled:opacity-50"
                  data-testid="forgot-submit-btn">
                  {loading ? "Updating..." : "Update password"}
                </button>
                <button type="button" onClick={() => setStage("email")}
                  className="w-full text-xs text-stone-500 hover:text-stone-900">
                  Didn&apos;t get the code? Try again
                </button>
              </form>
            </>
          )}

          {stage === "done" && (
            <div className="text-center py-4" data-testid="forgot-done">
              <h1 className="font-heading text-xl font-semibold text-stone-900 mb-2">Password updated</h1>
              <p className="font-body text-sm text-stone-500 mb-5">You can now sign in with your new password.</p>
              <button onClick={() => navigate("/login")}
                className="bg-emerald-700 text-white text-sm font-medium px-5 py-2 rounded-full">
                Go to sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
